import { adminUserService } from '@/services/adminUserService/adminUserService';
import { RootState } from '@/store';
import {
  AdminUserFilterParams,
  AdminUserInfo,
  AdminUserListResponse,
  AdminUsers,
  BlueNumberResponse,
  CreateAdminUser,
  MetaData,
  Roles,
  UpdateAdminUser,
} from '@/types/adminuser.types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AdminUserState {
  loading: boolean;
  error: string | null;
  data: AdminUsers[];
  meta: MetaData;
  userInfoCount: AdminUserInfo;
  selectedUser: AdminUsers | null;
  roles: Roles[];
  lookupData: BlueNumberResponse;
  blueNumberData: BlueNumberResponse | null;
  MobileNumberData: any;
}

const initialState: AdminUserState = {
  loading: false,
  error: null,
  data: [],
  meta: {
    totalPages: 0,
    currentPage: 0,
    pageSize: 0,
    totalCount: 0,
  },
  userInfoCount: {
    SUPER_ADMIN: 0,
    ADMIN: 0,
    VIEWER: 0,
    INACTIVE_ADMIN: 0,
  },
  selectedUser: null,
  roles: [],
  lookupData: {
    blueNumber: '',
    tenantIds: [],
  },
  blueNumberData: null,
  MobileNumberData: null,
};
//create User
export const createAdminUser = createAsyncThunk(
  'adminUsers/create',
  async (payload: CreateAdminUser, thunkAPI) => {
    try {
      const response = await adminUserService.create(payload);
      return response.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const createBlueNumber = createAsyncThunk<
  BlueNumberResponse,
  string,
  { rejectValue: string }
>('user/createBlueNumber', async (phoneNumber, { rejectWithValue }) => {
  try {
    const response = await adminUserService.createBlueNumber(phoneNumber);
    if (!response) {
      return rejectWithValue('Failed to create Blue Number Id');
    }
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch Blue Number Id');
  }
});

// get Blue number id by mobile number
export const fetchBlueNumberDetails = createAsyncThunk<
  BlueNumberResponse,
  string,
  { rejectValue: string }
>('user/fetchBlueNumberDetails', async (phoneNumber, { rejectWithValue }) => {
  try {
    const response = await adminUserService.getBlueNumberDetails(phoneNumber);
    if (!response) {
      return rejectWithValue('Failed to fetch Blue Number Id');
    }
    return response; // response itself contains { id, phoneNumber, blueNumberId }
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch Blue Number Id');
  }
});

export const fetchAdminUsers = createAsyncThunk<
  AdminUserListResponse,
  AdminUserFilterParams | undefined,
  { rejectValue: string }
>('adminUsers/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await adminUserService.getAll(params);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch admin users');
  }
});

//  Get Admin User Info Count

export const userInfo = createAsyncThunk('adminUsers/count', async (_, thunkAPI) => {
  try {
    const response = await adminUserService.getUserInfo();
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data || err.message);
  }
});

//  Get By ID
export const fetchAdminUserById = createAsyncThunk(
  'adminUsers/fetchById',
  async (id: number, thunkAPI) => {
    try {
      const response = await adminUserService.getById(id);
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

//Get all roles
export const getAllRoles = createAsyncThunk<Roles[], void, { state: RootState }>(
  'adminUsers/getAllRoles',
  async (_, thunkAPI) => {
    try {
      const response = await adminUserService.getAllRoles();
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { roles } = getState().user;

      if (roles.length > 0) {
        return false;
      }

      return true;
    },
  },
);

export const updateUser = createAsyncThunk(
  'adminUsers/update',
  async ({ id, payload }: { id: number; payload: UpdateAdminUser }, thunkAPI) => {
    try {
      const response = await adminUserService.update(id, payload);
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

// Delete
export const deleteUser = createAsyncThunk('adminUsers/delete', async (id: number, thunkAPI) => {
  try {
    const response = await adminUserService.remove(id);
    return response;
  } catch (err: any) {
    const apiMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Error deleting user';
    return thunkAPI.rejectWithValue(apiMessage);
  }
});

//
// ─── SLICE DEFINITION ───────────────────────────────────────────────
//

const adminUserSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addUserInList(state, action: PayloadAction<AdminUsers>) {
      state.data.unshift(action.payload);
      state.meta.totalCount += 1;
    },

    deleteUserInList(state, action: PayloadAction<number>) {
      state.data = state.data.filter((item) => item.id !== action.payload);
      if (state.meta.totalCount) state.meta.totalCount -= 1;
    },
    updateUserInList(state, action: PayloadAction<{ id: number; payload?: Partial<AdminUsers> }>) {
      const index = state.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = {
          ...state.data[index],
          ...action.payload.payload,
        };
      }
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAdminUsers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAdminUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload.data;
      state.meta = action.payload.meta;
    });
    builder.addCase(fetchAdminUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Error fetching admin users';
    });

    builder.addCase(userInfo.fulfilled, (state, action) => {
      state.userInfoCount = action.payload;
    });

    builder.addCase(fetchAdminUserById.fulfilled, (state, action) => {
      state.selectedUser = action.payload.data;
    });

    builder.addCase(createAdminUser.fulfilled, (state, action) => {
      if (state.data) state.data.push(action.payload);
    });

    builder
      .addCase(getAllRoles.fulfilled, (state, action: PayloadAction<Roles[]>) => {
        state.roles = action.payload;
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
      });

    builder
      .addCase(fetchBlueNumberDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchBlueNumberDetails.fulfilled, (state, action) => {
        state.lookupData = action.payload;
      })
      .addCase(fetchBlueNumberDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = 'Error fetching blueNumber ID';
      });

    builder.addCase(createBlueNumber.fulfilled, (state, action) => {
      state.loading = false;
      state.blueNumberData = action.payload;
    });
  },
});

export const { clearError, clearSelectedUser, addUserInList, updateUserInList, deleteUserInList } =
  adminUserSlice.actions;
export default adminUserSlice.reducer;
